import { Form, Formik } from "formik";
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useInView } from "react-intersection-observer";

import { useConfirm } from "../../hooks/confirm";
import useOrderFormService from "../../services/api/orderFormService";
import useCustomerService from "../../services/api/customerService";
import useCampaignService from "../../services/api/campaignService";
import usePrescriptionService from "../../services/api/prescriptionService";
import useOrderService from "../../services/api/orderService";
import { enums } from "../../constants/enum";
import dateFormatter from "../../util/dateFormatter";
import currencyFormatter from "../../util/currencyFormatter";

import {
  AddIcon,
  ArrowLongRightIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  FlagLineIcon,
  FlagSolidIcon,
  SearchIcon,
} from "../../assets/icons";
import {
  Button,
  Chip,
  IconButton,
  ListBox,
  PageTitle,
  Section,
  SectionSelector,
  Loading,
  Modal,
} from "../../components";
import {
  Checkbox,
  DateInput,
  EyeMeasurementInput,
  SelectInput,
  TextInput,
} from "../../components/forms";

import "./CampaignsPage.scss";

function CampaignDetailsPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [currentSection, setCurrentSection] = useState(0);
  const {
    getCampaignById,
    queryKey: campaignQueryKey,
    deleteCampaign,
  } = useCampaignService();
  const deleteCampaignMutation = useMutation({
    mutationFn: deleteCampaign,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [campaignQueryKey, { campaignId }],
    queryFn: () => getCampaignById(campaignId),
  });

  const handleDelete = async () => {
    const confirmDelete = await confirm({
      title: "Delete Campaign",
      description: "Are you sure you want to delete this campaign?",
    });
    if (!confirmDelete) return;
    deleteCampaignMutation.mutate(campaignId, {
      onSuccess: () => {
        navigate("/campaigns", { replace: true });
        toast.success("Campaign deleted successfully");
        queryClient.resetQueries({
          queryKey: [campaignQueryKey],
        });
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.payload?.message ||
            enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });
  };

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error("Failed to load campaign details");
    navigate("/campaigns", { replace: true });
  }

  return (
    <div className="page campaign-details-page">
      <PageTitle text="Campaign Details" prev="/campaigns">
        <div className="action-list-wide">
          <Button text="Add New Order" icon={<AddIcon />} />
          <Link to={`/campaigns/edit/${campaignId}`}>
            <Button text="Edit Details" icon={<EditIcon />} />
          </Link>
          <IconButton icon={<DeleteIcon />} rounded onClick={handleDelete} />
        </div>

        <ListBox
          className="action-list"
          options={[
            {
              text: "Add new order",
              icon: <AddIcon />,
              onClick: () => navigate(`/campaigns/add-order/${campaignId}`),
            },
            {
              text: "Edit details",
              icon: <EditIcon />,
              onClick: () => navigate(`/campaigns/edit/${campaignId}`),
            },
            {
              text: "Delete campaign",
              icon: <DeleteIcon />,
              onClick: handleDelete,
            },
          ]}
        />
      </PageTitle>

      <Section className="data-sec">
        <div className="left-bx">
          <div className="name">
            <span>Campaign Name</span>
            <h2>{data?.campaign?.name}</h2>
          </div>

          <div className="date">
            <span>Date</span>
            <p>{dateFormatter(data?.campaign?.date)}</p>
          </div>
        </div>

        <div className="right-bx">
          <div className="location">
            <span>Location</span>
            <p>{data?.campaign?.place}</p>
          </div>

          <div className="pref">
            <span>Order Number Prefix</span>
            <p>{data?.campaign?.orderNumberSequencePrefix}</p>
          </div>
        </div>
      </Section>

      <SectionSelector
        currentSection={currentSection}
        sectionList={[
          {
            text: "Customers",
            count: `${String(data?.campaign?.totalCustomers).padStart(2, "0")}`,
          },
          {
            text: "Prescription Forms",
            count: `${String(data?.campaign?.totalOrderForms).padStart(
              2,
              "0",
            )}`,
          },
          {
            text: "Orders",
            count: `${String(data?.campaign?.totalOrders).padStart(2, "0")}`,
          },
        ]}
        onSectionChange={(next) => setCurrentSection(next)}
      />

      {currentSection === 0 ? (
        <CampaignCustomers campaignId={campaignId} />
      ) : null}

      {currentSection === 1 ? (
        <CampaignPrescriptions
          campaignId={campaignId}
          campaignPrefix={data?.campaign?.orderNumberSequencePrefix}
        />
      ) : null}

      {currentSection === 2 ? <CampaignOrders campaignId={campaignId} /> : null}
    </div>
  );
}

export default CampaignDetailsPage;

function CampaignCustomers({ campaignId }) {
  const navigate = useNavigate();
  const { getCustomersByCampaignId, queryKey } = useCustomerService();
  const { ref, inView } = useInView();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    data: customersData,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [queryKey, { campaignId, searchQuery }],
    queryFn: ({ pageParam = 1 }) =>
      getCustomersByCampaignId(campaignId, searchQuery, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.totalPages > lastPage?.currentPage) {
        return lastPage?.currentPage + 1;
      }
      return undefined;
    },
    enabled: !!campaignId,
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const handleSearch = (query) => {
    if (!query) {
      setSearchQuery("");
    } else {
      setSearchQuery(query);
    }
    refetch();
  };

  return (
    <Section className="customers-section" title="Customers">
      <div className="filter-form">
        <Formik
          initialValues={{ query: "" }}
          onSubmit={(values) => handleSearch(values.query)}
        >
          <Form>
            <TextInput
              name="query"
              type="search"
              label="Search by name or phone number"
              placeholder="Enter customer name or phone number"
            />
            <div className="form-actions">
              <Button type="submit" icon={<SearchIcon />} text="Search" />
              <Button
                type="reset"
                icon={<CloseIcon />}
                text="Clear"
                outlined
                className="clear-btn"
              />
            </div>
          </Form>
        </Formik>
      </div>

      <div className="table-wrapper">
        <div className="table-title">
          <h3>All Campaign Customers</h3>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th>Customer ID</th>
                <th>Name</th>
                <th>Mobile Number</th>
                <th>Last Order</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {customersData?.pages.map(({ customers }) =>
                customers.map((customer) => (
                  <tr key={customer._id}>
                    <td>{customer._id}</td>
                    <td>{customer.nameWithTitle}</td>
                    <td>{customer.mobileNumber}</td>
                    <td>{dateFormatter(customer?.lastOrderDate)}</td>
                    <td>
                      <Link to={`/customers/details/${customer?._id}`}>
                        <Button
                          text="View"
                          prefixIcon={<ArrowLongRightIcon />}
                        />
                      </Link>
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            ref={ref}
          >
            {isFetchingNextPage ? <Loading variant="overlay" /> : null}
          </div>
        </div>
      </div>
    </Section>
  );
}

function CampaignPrescriptions({ campaignId, campaignPrefix }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { ref, inView } = useInView();
  const { getPrescriptions, queryKey } = usePrescriptionService();
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null);

  const { data, fetchNextPage, isFetchingNextPage, refetch, error, isLoading } =
    useInfiniteQuery({
      queryKey: [queryKey, { campaignId, searchQuery, from, to }],
      queryFn: ({ pageParam = 1 }) =>
        getPrescriptions(campaignId, searchQuery, from, to, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
      enabled: !!campaignId,
      retry: 1,
    });
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  if (isLoading) {
    return <Loading variant="overlay" />;
  }

  if (error) {
    toast.error(error?.message);
  }

  function handleSearch(values) {
    const { query, from, to } = values;
    if (query) {
      setSearchQuery(query.match(/(\d+(\.\d+)?)/g)?.[0]);
    } else {
      setSearchQuery("");
    }
    if (from) {
      setFrom(from);
    } else {
      setFrom("");
    }
    if (to) {
      setTo(to);
    } else {
      setTo("");
    }
    refetch();
  }
  return (
    <>
      <Section
        className="prescription-forms-section"
        title="Prescription Forms"
      >
        <div className="filter-form">
          <Formik
            initialValues={{ query: "", from: "", to: "" }}
            onSubmit={handleSearch}
          >
            <Form>
              <TextInput
                name="query"
                label="Search by order number"
                placeholder="Enter order number"
                type="search"
              />

              <div className="input-group">
                <DateInput name="from" label="From" />
                <DateInput name="to" label="To" />
              </div>

              <div className="form-actions">
                <Button type="submit" icon={<SearchIcon />} text="Search" />
                <Button
                  type="reset"
                  icon={<CloseIcon />}
                  text="Clear"
                  outlined
                  className="clear-btn"
                />
              </div>
            </Form>
          </Formik>
        </div>

        <div className="table-wrapper">
          <div className="table-title">
            <h3>Prescription Forms</h3>
          </div>

          <div className="table-content">
            <table>
              <thead>
                <tr>
                  <th>Prescription ID</th>
                  <th>Created date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data?.pages?.map(({ orderForms }) =>
                  orderForms?.map((orderForm) => (
                    <tr key={orderForm._id}>
                      <td>
                        {campaignPrefix}-{orderForm.eyeCheckingNumber}
                      </td>
                      <td>{dateFormatter(orderForm.createdAt)}</td>
                      <td>
                        <Button
                          text="View"
                          prefixIcon={<ArrowLongRightIcon />}
                          onClick={() => {
                            setSelectedPrescriptionId(orderForm._id);
                            setShowPrescriptionModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              ref={ref}
            >
              {isFetchingNextPage ? <Loading variant="inline" /> : null}
            </div>
          </div>
        </div>
      </Section>
      <PrescriptionModal
        showPrescriptionModal={showPrescriptionModal}
        setShowPrescriptionModal={setShowPrescriptionModal}
        prescriptionId={selectedPrescriptionId}
      />
    </>
  );
}

function CampaignOrders({ campaignId }) {
  const [orderNumber, setOrderNumber] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const orderStatusList = Object.values(enums.ORDER_STATUSES).map((status) => ({
    label: status,
    value: status,
  }));
  const { ref, inView } = useInView();
  const { getOrders, queryKey, toggleOrderFlag } = useOrderService();

  const mutation = useMutation({
    mutationFn: toggleOrderFlag,
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const { data, fetchNextPage, isFetchingNextPage, refetch, error, isLoading } =
    useInfiniteQuery({
      queryKey: [queryKey, { campaignId, orderNumber, status, from, to }],
      queryFn: ({ pageParam = 1 }) =>
        getOrders(status, from, to, orderNumber, campaignId, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
      enabled: !!campaignId,
      retry: 1,
    });
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const handleFlagToggle = useCallback(
    (orderId) => {
      mutation.mutate(orderId);
    },
    [mutation],
  );

  if (isLoading) {
    return <Loading variant="overlay" />;
  }
  if (error) {
    toast.error(error?.message);
  }
  function setChipColor(status) {
    switch (status) {
      case enums.ORDER_STATUSES.CONFIRMED:
        return "warning";
      case enums.ORDER_STATUSES.READY:
        return "info";
      case enums.ORDER_STATUSES.COMPLETED:
        return "success";
      case enums.ORDER_STATUSES.CANCELLED:
        return "danger";
      default:
        return undefined;
    }
  }

  function handleSearch(values) {
    const { orderNumber, orderStatus, from, to } = values;
    setOrderNumber(orderNumber ? orderNumber : "");
    setStatus(orderStatus ? orderStatus : "");
    setFrom(from ? from : "");
    setTo(to ? to : "");
    refetch();
  }

  return (
    <Section className="orders-section" title="Orders">
      <div className="filter-form">
        <Formik
          initialValues={{
            orderNumber: "",
            orderStatus: "",
            from: "",
            to: "",
          }}
          onSubmit={handleSearch}
        >
          <Form>
            <div className="input-group">
              <TextInput
                type="search"
                name="orderNumber"
                label="Search by order number"
                placeholder="Enter order number"
              />
              <SelectInput
                name="orderStatus"
                values={orderStatusList}
                label="Order status"
              />
            </div>

            <div className="input-group">
              <DateInput name="from" label="From" />
              <DateInput name="to" label="To" />
            </div>

            <div className="form-actions">
              <Button type="submit" icon={<SearchIcon />} text="Search" />
              <Button
                type="reset"
                icon={<CloseIcon />}
                text="Clear"
                outlined
                className="clear-btn"
              />
            </div>
          </Form>
        </Formik>
      </div>

      <div className="table-wrapper">
        <div className="table-title">
          <h3>All Orders</h3>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th>Flag</th>
                <th>Order ID</th>
                <th>Order Date</th>
                <th>Order Amount</th>
                <th>Prescription ID</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {data?.pages?.map(({ orders }) =>
                orders?.map((order) => (
                  <tr key={order?.orderId}>
                    <td>
                      <button
                        className="flag-btn"
                        onClick={() => handleFlagToggle(order?.orderId)}
                      >
                        {order?.isFlagged ? (
                          <FlagSolidIcon />
                        ) : (
                          <FlagLineIcon />
                        )}
                      </button>
                    </td>
                    <td>{order?.orderId}</td>
                    <td>{dateFormatter(order?.orderDate)}</td>
                    <td>{currencyFormatter(order?.totalAmount)}</td>
                    <td>{order?.orderForm?.eyeCheckingNumber}</td>
                    <td>
                      <Chip
                        text={order?.status}
                        variant={setChipColor(order?.status)}
                      />
                    </td>
                    <td>
                      <Link to={`/orders/details/${order?.orderId}`}>
                        <Button
                          text="View"
                          prefixIcon={<ArrowLongRightIcon />}
                        />
                      </Link>
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            ref={ref}
          >
            {isFetchingNextPage ? <Loading variant="inline" /> : null}
          </div>
        </div>
      </div>
    </Section>
  );
}
function PrescriptionModal({
  showPrescriptionModal,
  setShowPrescriptionModal,
  prescriptionId,
}) {
  const { getOrderFormById, queryKey } = useOrderFormService();
  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, prescriptionId],
    queryFn: () => getOrderFormById(prescriptionId),
    enabled: !!prescriptionId,
  });

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  const initialValues = {
    eyeCheckingNumber: data?.orderForm?.eyeCheckingNumber,
    CR30: data?.orderForm?.CR30 ? data?.orderForm?.CR30 : "",
    PHOTO_GREY: data?.orderForm?.PHOTO_GREY ? data?.orderForm?.PHOTO_GREY : "",
    MULTI_COATED: data?.orderForm?.MULTI_COATED
      ? data?.orderForm?.MULTI_COATED
      : "",
    PROGRESSIVE: data?.orderForm?.PROGRESSIVE
      ? data?.orderForm?.PROGRESSIVE
      : "",
    EASY_FIT: data?.orderForm?.EASY_FIT ? data?.orderForm?.EASY_FIT : "",
    hb: data?.orderForm?.hb || "",
    distance: {
      right: {
        sph: data?.orderForm?.distance?.right?.sph || null,
        cyl: data?.orderForm?.distance?.right?.cyl || null,
        axis: data?.orderForm?.distance?.right?.axis || 0,
      },
      left: {
        sph: data?.orderForm?.distance?.left?.sph || null,
        cyl: data?.orderForm?.distance?.left?.cyl || null,
        axis: data?.orderForm?.distance?.left?.axis || 0,
      },
    },
    near: {
      right: {
        sph: data?.orderForm?.near?.right?.sph || null,
        cyl: data?.orderForm?.near?.right?.cyl || null,
        axis: data?.orderForm?.near?.right?.axis || 0,
      },
      left: {
        sph: data?.orderForm?.near?.left?.sph || null,
        cyl: data?.orderForm?.near?.left?.cyl || null,
        axis: data?.orderForm?.near?.left?.axis || 0,
      },
    },
    frame: null,
    lense: null,
    discount: 0,
    orderSchedule: "now",
    paymentType: "fullPayment",
    paymentMethod: enums.PAYMENT_METHODS.CASH,
  };
  return (
    <Modal
      className="prescription-modal"
      title="Prescription"
      close={!showPrescriptionModal}
      onClose={() => setShowPrescriptionModal(false)}
    >
      <Formik initialValues={initialValues} enableReinitialize>
        <Form>
          <Section
            title="Prescription Details"
            className="prescription-details"
          >
            <TextInput
              className="prescription-number-input"
              name="eyeCheckingNumber"
              disabled
              label="Prescription Number"
            />

            <div className="meta-box">
              <div className="date">
                <span>Date</span>
                <p>{dateFormatter(data?.orderForm?.testDate)}</p>
              </div>

              {/* <div className="created">
                  <span>Created by</span>
                  <p>
                    <span className="role">Admin</span> Mr. Kalpa Kavindu
                  </p>
                </div> */}
            </div>
          </Section>

          <Section
            title="Eye Measurements"
            className="eye-measurements-section"
          >
            <div className="input-group">
              <TextInput
                label="Half Bifocal (H.B)"
                placeholder="Type here"
                name="hb"
                prefix="+"
                disabled
              />

              <div className="lens-options">
                <span>Lens options</span>

                <div>
                  <div className="checkbox-group">
                    <Checkbox name="CR30" disabled label="CR30" />
                    <Checkbox name="PHOTO_GREY" disabled label="Photo Grey" />
                    <Checkbox
                      name="MULTI_COATED"
                      disabled
                      label="Multi Coated"
                    />
                  </div>

                  <div className="checkbox-group">
                    <Checkbox name="PROGRESSIVE" disabled label="Progressive" />
                    <Checkbox name="EASY_FIT" disabled label="Easy Fit" />
                  </div>
                </div>
              </div>
            </div>

            <div className="table-group">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>R</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.right.sph"
                        />
                      </td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.right.cyl"
                        />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="distance.right.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput disabled name="near.right.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput disabled name="near.right.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="near.right.axis"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>L</th>
                      <th>SPH</th>
                      <th>CYL</th>
                      <th>AXIS</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>DIST</td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.left.sph"
                        />
                      </td>
                      <td>
                        <EyeMeasurementInput
                          disabled
                          name="distance.left.cyl"
                        />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="distance.left.axis"
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>NEAR</td>
                      <td>
                        <EyeMeasurementInput disabled name="near.left.sph" />
                      </td>
                      <td>
                        <EyeMeasurementInput disabled name="near.left.cyl" />
                      </td>
                      <td>
                        <TextInput
                          className="tinput"
                          disabled
                          name="near.left.axis"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
          {data?.orderForm?.lense ? (
            <Section title="Lens Details" className="lens-details-section">
              <div className="right-lens">
                <h3>Right Lens</h3>
                <div className="lens-table">
                  <div className="table-container">
                    <table>
                      <tbody>
                        <tr>
                          <td
                            rowSpan={
                              data?.orderForm?.lense?.right?.selectedLense
                                ?.attributes?.coating ===
                              enums.LENSE_COATINGS.PHOTO_GREY
                                ? 6
                                : 5
                            }
                            className="table-title"
                          >
                            <p>LENS</p>
                          </td>
                          <td className="table-sub-title">Brand</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.brand || " - "}
                          </td>
                        </tr>

                        <tr>
                          <td className="table-sub-title">Type</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.lense_type || " - "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Color</td>
                          <td>
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.coating || " - "}
                          </td>
                        </tr>

                        {data?.orderForm?.lense?.right?.selectedLense
                          ?.attributes?.coating ===
                        enums.LENSE_COATINGS.PHOTO_GREY ? (
                          <tr>
                            <td className="table-sub-title">Transition</td>
                            <td>
                              {data?.orderForm?.lense?.right?.selectedLense
                                ?.attributes?.transition || " - "}
                            </td>
                          </tr>
                        ) : null}
                        <tr>
                          <td className="table-sub-title">SEG</td>
                          <td>
                            24 x{" "}
                            {data?.orderForm?.lense?.right?.selectedLense
                              ?.attributes?.seg
                              ? `${data?.orderForm?.lense?.right?.selectedLense?.attributes?.seg} mm`
                              : "  "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Price</td>
                          <td>
                            {currencyFormatter(
                              parseInt(
                                data?.orderForm?.lense?.right?.selectedLense
                                  ?.sellingPrice || 0,
                              ),
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="separator"></div>

              <div className="left-lens">
                <h3>Left Lens</h3>
                <div className="lens-table">
                  <div className="table-container">
                    <table>
                      <tbody>
                        <tr>
                          <td
                            rowSpan={
                              data?.orderForm?.lense?.left?.selectedLense
                                ?.attributes?.coating ===
                              enums.LENSE_COATINGS.PHOTO_GREY
                                ? 6
                                : 5
                            }
                            className="table-title"
                          >
                            <p>LENS</p>
                          </td>
                          <td className="table-sub-title">Brand</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.brand || " - "}
                          </td>
                        </tr>

                        <tr>
                          <td className="table-sub-title">Type</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.lense_type || " - "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Color</td>
                          <td>
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.coating || " - "}
                          </td>
                        </tr>

                        {data?.orderForm?.lense?.left?.selectedLense?.attributes
                          ?.coating === enums.LENSE_COATINGS.PHOTO_GREY ? (
                          <tr>
                            <td className="table-sub-title">Transition</td>
                            <td>
                              {data?.orderForm?.lense?.left?.selectedLense
                                ?.attributes?.transition || " - "}
                            </td>
                          </tr>
                        ) : null}
                        <tr>
                          <td className="table-sub-title">SEG</td>
                          <td>
                            24 x{" "}
                            {data?.orderForm?.lense?.left?.selectedLense
                              ?.attributes?.seg
                              ? `${data?.orderForm?.lense?.left?.selectedLense?.attributes?.seg} mm`
                              : "  "}
                          </td>
                        </tr>
                        <tr>
                          <td className="table-sub-title">Price</td>
                          <td>
                            {currencyFormatter(
                              parseInt(
                                data?.orderForm?.lense?.left?.selectedLense
                                  ?.sellingPrice || 0,
                              ),
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Section>
          ) : null}
          {data?.orderForm?.frame ? (
            <Section title="Frame Details" className="frame-details-section">
              <div className="frame-table">
                <div className="table-container">
                  <table>
                    <tr>
                      <td rowSpan={8} className="table-title">
                        <p>FRAME</p>
                      </td>
                      <td className="table-sub-title">Modal</td>
                      <td colSpan={2}>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.model_number
                        }
                      </td>
                    </tr>

                    <tr>
                      <td rowSpan={3} className="table-sub-title">
                        Size
                      </td>
                      <td className="table-sub-sub-title">Lens diagonal</td>
                      <td>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.size?.lens_diagonal
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-sub-title">Bridge size</td>
                      <td>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.size?.bridge
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-sub-title">Arm Length</td>
                      <td>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.size?.arm_length
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">Material</td>
                      <td colSpan={2}>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.material
                        }
                      </td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Color</td>
                      <td colSpan={2}>
                        {
                          data?.orderForm?.frame?.selectedFrame?.attributes
                            ?.color
                        }
                      </td>
                    </tr>

                    <tr>
                      <td className="table-sub-title">P.D</td>
                      <td colSpan={2}>{data?.orderForm?.frame?.pd}</td>
                    </tr>
                    <tr>
                      <td className="table-sub-title">Price</td>
                      <td colSpan={2}>
                        {currencyFormatter(
                          parseInt(
                            data?.orderForm?.frame?.selectedFrame
                              ?.sellingPrice || 0,
                          ),
                        )}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </Section>
          ) : null}
        </Form>
      </Formik>
    </Modal>
  );
}
